import type { CharacterArt } from '../characters/CharacterTypes';
import type { UiConfig } from '../config/ui.config';
import { el } from './dom';
import { ICONS, icon } from './icons';
import { menuFooter, menuTitle } from './MenuParts';

/** Short how-to-play row under the progress bar. */
const TIPS = [
  [ICONS.react, 'Reagieren'],
  [ICONS.collect, 'Sammeln'],
  [ICONS.dodge, 'Ausweichen'],
  [ICONS.survive, 'Überleben'],
] as const;

const NBSP = '\u00a0'; // keeps "68 %" on one line

/** "Lädt … 68 %" text of a progress fraction. */
export function loadingLabel(fraction: number): string {
  const percent = Math.round(Math.min(Math.max(fraction, 0), 1) * 100);
  return `Lädt … ${percent}${NBSP}%`;
}

/**
 * Loading screen on the blurred start scene: logo, progress bar of the asset preload, how-to-play icons and the
 * standing figure of the selected character (desktop: large on the right, phones: centred above the bar).
 */
export class LoadingScreen {
  readonly element: HTMLDivElement;
  private readonly bar: HTMLDivElement;
  private readonly fill: HTMLDivElement;
  private readonly label: HTMLParagraphElement;

  constructor(parent: HTMLElement, ui: UiConfig, art: CharacterArt | null) {
    this.element = el('div', 'screen menu menu-loading', undefined, parent);
    menuTitle(this.element, ui);
    if (art) {
      const figure = el('img', 'menu-figure loading-figure', undefined, this.element);
      figure.src = art.figureUrl;
      figure.alt = art.alt;
      figure.decoding = 'async';
    }
    const progress = el('div', 'loading-progress', undefined, this.element);
    this.bar = el('div', 'loading-bar', undefined, progress);
    this.bar.setAttribute('role', 'progressbar');
    this.bar.setAttribute('aria-label', 'Spiel wird geladen');
    this.bar.setAttribute('aria-valuemin', '0');
    this.bar.setAttribute('aria-valuemax', '100');
    this.fill = el('div', 'loading-bar-fill', undefined, this.bar);
    this.label = el('p', 'loading-label', undefined, progress);
    const tips = el('ul', 'loading-tips', undefined, this.element);
    for (const [markup, text] of TIPS) {
      const item = el('li', 'loading-tip', undefined, tips);
      item.append(icon(markup, 'loading-tip-icon'));
      el('span', undefined, text, item);
    }
    menuFooter(this.element, ui.footer);
    this.setProgress(0);
  }

  /** Asset preload progress, 0..1. */
  setProgress(fraction: number): void {
    const percent = Math.round(Math.min(Math.max(fraction, 0), 1) * 100);
    this.fill.style.width = `${percent}%`;
    this.bar.setAttribute('aria-valuenow', String(percent));
    this.label.textContent = loadingLabel(fraction);
  }
}
